import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Download, Upload, Check, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

export const JsonFormatterTool: React.FC = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatJson = (jsonString: string, indent: number = 2) => {
    try {
      const parsed = JSON.parse(jsonString);
      const formatted = JSON.stringify(parsed, null, indent);
      setOutput(formatted);
      setError('');
      setIsValid(true);
      return formatted;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('tools.jsonFormatter.invalidJson');
      setError(errorMessage);
      setOutput('');
      setIsValid(false);
      return null;
    }
  };

  const minifyJson = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      const minified = JSON.stringify(parsed);
      setOutput(minified);
      setError('');
      setIsValid(true);
      return minified;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('tools.jsonFormatter.invalidJson');
      setError(errorMessage);
      setOutput('');
      setIsValid(false);
      return null;
    }
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    if (value.trim()) {
      formatJson(value);
    } else {
      setOutput('');
      setError('');
      setIsValid(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const downloadJson = () => {
    if (output) {
      const blob = new Blob([output], { type: 'application/json' });
      saveAs(blob, t('tools.jsonFormatter.defaultFilename'));
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInput(content);
        formatJson(content);
      };
      reader.readAsText(file);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
    setIsValid(false);
  };

  const validateJsonSchema = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);

      // Basic schema validation
      const analysis = {
        type: Array.isArray(parsed) ? 'array' : typeof parsed,
        keys: typeof parsed === 'object' && parsed !== null ? Object.keys(parsed).length : 0,
        depth: getObjectDepth(parsed),
        hasNulls: hasNullValues(parsed),
        hasArrays: hasArrayValues(parsed),
        hasObjects: hasObjectValues(parsed)
      };

      return analysis;
    } catch {
      return null;
    }
  };

  const getObjectDepth = (obj: any, depth = 0): number => {
    if (obj === null || typeof obj !== 'object') return depth;

    if (Array.isArray(obj)) {
      return Math.max(depth, ...obj.map(item => getObjectDepth(item, depth + 1)));
    }

    const depths = Object.values(obj).map(value => getObjectDepth(value, depth + 1));
    return depths.length > 0 ? Math.max(depth, ...depths) : depth;
  };

  const hasNullValues = (obj: any): boolean => {
    if (obj === null) return true;
    if (typeof obj !== 'object') return false;

    if (Array.isArray(obj)) {
      return obj.some(hasNullValues);
    }

    return Object.values(obj).some(hasNullValues);
  };

  const hasArrayValues = (obj: any): boolean => {
    if (Array.isArray(obj)) return true;
    if (typeof obj !== 'object' || obj === null) return false;

    return Object.values(obj).some(hasArrayValues);
  };

  const hasObjectValues = (obj: any): boolean => {
    if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) return true;
    if (Array.isArray(obj)) {
      return obj.some(hasObjectValues);
    }
    if (typeof obj !== 'object' || obj === null) return false;

    return Object.values(obj).some(hasObjectValues);
  };

  const sortJsonKeys = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      const sorted = sortObjectKeys(parsed);
      const formatted = JSON.stringify(sorted, null, 2);
      setOutput(formatted);
      setError('');
      setIsValid(true);
      return formatted;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('tools.jsonFormatter.invalidJson');
      setError(errorMessage);
      setOutput('');
      setIsValid(false);
      return null;
    }
  };

  const sortObjectKeys = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(sortObjectKeys);
    }

    if (obj !== null && typeof obj === 'object') {
      const sorted: any = {};
      Object.keys(obj).sort().forEach(key => {
        sorted[key] = sortObjectKeys(obj[key]);
      });
      return sorted;
    }

    return obj;
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>{t('tools.jsonFormatter.name')}</span>
            {isValid && <Check className="w-5 h-5 text-green-500" />}
            {error && <AlertCircle className="w-5 h-5 text-red-500" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={() => input && formatJson(input, 2)}
              disabled={!input}
            >
              {t('tools.jsonFormatter.format2Spaces')}
            </Button>
            <Button
              onClick={() => input && formatJson(input, 4)}
              disabled={!input}
              variant="secondary"
            >
              {t('tools.jsonFormatter.format4Spaces')}
            </Button>
            <Button
              onClick={() => input && minifyJson(input)}
              disabled={!input}
              variant="secondary"
            >
              {t('tools.jsonFormatter.minify')}
            </Button>
            <Button
              onClick={() => input && sortJsonKeys(input)}
              disabled={!input}
              variant="secondary"
            >
              {t('tools.jsonFormatter.sortKeys')}
            </Button>
            <Button
              onClick={() => output && copyToClipboard(output)}
              disabled={!output}
              variant="outline"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('common.copy')}
            </Button>
            <Button
              onClick={downloadJson}
              disabled={!output}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('common.download')}
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              {t('common.clear')}
            </Button>
          </div>
          
          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="mb-2"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.selectFile')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Input/Output */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.jsonFormatter.inputJson')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={t('tools.jsonFormatter.placeholder')}
              className="min-h-[400px] font-mono text-sm"
            />
            {error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                  <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.jsonFormatter.outputJson')}</span>
              {output && (
                <span className="text-sm text-gray-500">
                  {output.split('\n').length} {t('tools.jsonFormatter.lines')}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder={t('tools.jsonFormatter.outputPlaceholder')}
              className="min-h-[400px] font-mono text-sm bg-gray-50 dark:bg-gray-800"
            />
          </CardContent>
        </Card>
      </div>

      {/* JSON Info */}
      {isValid && output && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.jsonFormatter.analysis')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-6">
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.jsonFormatter.characters')}:</span>
                <div className="font-mono">{output.length.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.jsonFormatter.lines')}:</span>
                <div className="font-mono">{output.split('\n').length}</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.jsonFormatter.size')}:</span>
                <div className="font-mono">{(new Blob([output]).size / 1024).toFixed(2)} {t('tools.jsonFormatter.kb')}</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.jsonFormatter.valid')}:</span>
                <div className="flex items-center">
                  <Check className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-green-600 dark:text-green-400">{t('tools.jsonFormatter.yes')}</span>
                </div>
              </div>
            </div>

            {(() => {
              const analysis = validateJsonSchema(input);
              if (!analysis) return null;

              return (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">{t('tools.jsonFormatter.structureAnalysis')}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.jsonFormatter.type')}:</span>
                      <div className="font-mono capitalize">{analysis.type}</div>
                    </div>
                    {analysis.type === 'object' && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">{t('tools.jsonFormatter.keys')}:</span>
                        <div className="font-mono">{analysis.keys}</div>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.jsonFormatter.depth')}:</span>
                      <div className="font-mono">{analysis.depth}</div>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.jsonFormatter.hasArrays')}:</span>
                      <div className="font-mono">{analysis.hasArrays ? t('tools.jsonFormatter.yes') : t('tools.jsonFormatter.no')}</div>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.jsonFormatter.hasObjects')}:</span>
                      <div className="font-mono">{analysis.hasObjects ? t('tools.jsonFormatter.yes') : t('tools.jsonFormatter.no')}</div>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.jsonFormatter.hasNulls')}:</span>
                      <div className="font-mono">{analysis.hasNulls ? t('tools.jsonFormatter.yes') : t('tools.jsonFormatter.no')}</div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
